import { useState } from 'react';
import { Save, X, CheckCircle2, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';
import { reportTemplatesApi, TEMPLATE_CATEGORIES, type Template } from '../lib/reportTemplatesApi';

interface Props {
  reportId: number;
  isAdmin: boolean;
  open: boolean;
  onClose: () => void;
  onSaved: (template: Template) => void;
}

export default function SaveAsTemplateModal({
  reportId,
  isAdmin,
  open,
  onClose,
  onSaved,
}: Props) {
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>('');
  const [asSystem, setAsSystem] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Template | null>(null);

  if (!open) return null;

  const canSave = name.trim().length > 0 && !saving;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: { name: string; description?: string; scope?: 'system' | 'user'; category?: string | null } = {
        name: name.trim(),
        description: description.trim(),
        category: category || null,
      };
      if (isAdmin && asSystem) payload.scope = 'system';
      const result = await reportTemplatesApi.createFromReport(reportId, payload);
      onSaved(result);
      setSaved(result);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Speichern fehlgeschlagen';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setName('');
    setDescription('');
    setCategory('');
    setAsSystem(false);
    setSaved(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200';
  const input = darkMode
    ? 'bg-slate-700 border-slate-600 text-white'
    : 'bg-white border-slate-300';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  if (saved) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className={`rounded-xl border shadow-xl p-6 max-w-md w-full ${card}`}>
          <div className="flex items-start gap-3 mb-4">
            <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold">Als Vorlage gespeichert</h2>
              <p className={`text-sm mt-1 ${muted}`}>„{saved.name}" wurde als Vorlage gespeichert. Du bleibst im Bericht.</p>
            </div>
          </div>
          <p className={`text-xs mb-6 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
            Prüfe die übernommenen Texte vor einer späteren Wiederverwendung auf kundenspezifische Angaben.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={handleClose}
              className={`px-4 py-2 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
            >
              Schließen
            </button>
            <button
              onClick={() => {
                const id = saved.id;
                reset();
                onClose();
                navigate(`/templates?edit=${id}`);
              }}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <Pencil className="w-4 h-4" />
              Vorlage bearbeiten
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`rounded-xl border shadow-xl p-6 max-w-md w-full ${card}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Als Vorlage speichern</h2>
          <button onClick={handleClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="tpl-name" className="block text-sm font-medium mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="tpl-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${input}`}
              placeholder="z.B. BAFA Standard-Digital"
            />
          </div>

          <div>
            <label htmlFor="tpl-desc" className="block text-sm font-medium mb-1">
              Beschreibung (optional)
            </label>
            <textarea
              id="tpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${input}`}
              placeholder="Wofür ist diese Vorlage gut?"
            />
          </div>

          <div>
            <label htmlFor="tpl-category" className="block text-sm font-medium mb-1">
              Kategorie (optional)
            </label>
            <select
              id="tpl-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm ${input}`}
            >
              <option value="">Ohne Kategorie</option>
              {TEMPLATE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {isAdmin && (
            <div className={`p-3 rounded-lg border ${darkMode ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200'}`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  id="tpl-system"
                  aria-label="System-Vorlage"
                  type="checkbox"
                  checked={asSystem}
                  onChange={(e) => setAsSystem(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">
                  Als System-Vorlage (sichtbar für alle freigeschalteten User)
                </span>
              </label>
              {asSystem && (
                <p className={`text-xs mt-1 ml-6 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Sicher? Diese Vorlage erscheint dann bei allen Beratern in der Auswahl.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={handleClose}
            className={`px-4 py-2 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
